<template>
  <div class="calendar__container" ref="rootEl">
    <HeaderBar
      title="Календарь"
      :showButtons="false"
      :ranges="ranges"
      @change-range="onChangeRange"
      @refresh="onRefresh"
      :statistic="statistic"
    />

    <div class="page">
      <aside class="sidebar">
        <h2>Мои события</h2>

        <ul class="events-list" v-if="items.length">
          <li
            v-for="ev in itemsSorted"
            :key="ev.id"
            @click="openEvent(ev)"
            class="events-list__item"
          >
            <div class="dot" :style="{ backgroundColor: ev.color || defaultColor }"></div>
            <div class="meta">
              <div class="title">{{ ev.title }}</div>
              <div class="date">
                {{ formatShort(ev.startDate) }}
                <span v-if="ev.endDate && ev.endDate !== ev.startDate"> — {{ formatShort(ev.endDate) }}</span>
              </div>
            </div>
          </li>
        </ul>

        <div v-else class="empty">Событий пока нет — заявки не найдены в <code>requests</code>!</div>

        <!-- Добавление/редактирование событий полностью закомментировано по запросу -->
        <!--
        <button class="btn primary" @click="openAddDialog()">Добавить событие</button>
        <small class="muted">Нажмите на день в календаре для быстрого добавления.</small>
        -->
      </aside>

      <main class="main">
        <CalendarView
          :show-date="showDate"
          :items="items"
          class="theme-default holiday-us-traditional holiday-us-official nice-calendar"
          :show-times="true"
          @click-date="onClickDate"
          @click-item="onClickItem"
          :style="cssVars"
        >
          <template #header="{ headerProps }">
            <CalendarViewHeader
              :header-props="headerProps"
              :currentPeriodLabel="'Сегодня'"
              @input="onHeaderInput"
              class="custom-header"
            />
          </template>
        </CalendarView>
      </main>

      <!-- модалки просмотра события (редактирование/добавление закомментированы) -->

      <!-- Просмотр события остаётся -->
      <div v-if="viewEventVisible" class="modal-backdrop" @click.self="viewEventVisible = false">
        <div class="modal">
          <h3>{{ activeEvent.title }}</h3>
          <p class="modal__p"><strong>Дата:</strong></p>
          <p class="modal__p">
            <span>{{ formatFull(activeEvent.startDate) }}</span>
            <span v-if="activeEvent.endDate && activeEvent.endDate !== activeEvent.startDate"> — {{ formatFull(activeEvent.endDate) }}</span>
          </p>
          <div v-if="activeEvent.tooltip">
            <p class="modal__p"><strong>Описание:</strong></p> 
            <p class="modal__p" v-html="activeEvent.tooltip"></p>
          </div>

          <div class="modal-actions">
            <button class="btn" @click="viewEventVisible = false">Закрыть</button>
            <!-- Редактирование закомментировано -->
            <!-- <button class="btn primary" @click="editEvent(activeEvent)">Редактировать</button> -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import { CalendarView, CalendarViewHeader } from 'vue-simple-calendar'
import 'vue-simple-calendar/dist/vue-simple-calendar.css'
import 'vue-simple-calendar/dist/css/default.css'
import 'vue-simple-calendar/dist/css/holidays-us.css'
import HeaderBar from './HeaderBar.vue'

export default {
  name: 'calendar',
  components: { CalendarView, CalendarViewHeader, HeaderBar },
  props: ['statistic', 'requests'],
  emits: ['change-range','refresh','open-event','date-clicked','items-updated'],
  data() {
    const d = new Date()
    const iso = d.toISOString().slice(0,10)
    return {
      defaultColor: '#3b82f6',

      showDate: new Date(),
      items: [],

      viewEventVisible: false,
      activeEvent: null,

      ranges: [
        { key: 'today', title: 'Сегодня' },
        { key: 'last_7_days', title: 'За 7 дней' },
        { key: 'current_week', title: 'За неделю' },
        { key: 'current_month', title: 'За месяц' },
        { key: 'last_90_days', title: 'За 90 дней' },
        { key: 'current_year', title: 'За год' },
        { key: 'all_time', title: 'За все время' }
      ],

      // sizing (px)
      baseDayHeight: 80,
      dayMinHeightDefault: 80,

      calendarMinHeight: 600,
      headerHeightEstimate: 92,
      additionalPadding: 40,

      // карточка/стек (px)
      itemMinHeightPx: 48,
      gapPx: 14,
      extraStackOffset: 40,

      libraryTopEm: 2.8,
      libraryTopExtraPx: 2,

      // высота пустой недели в rem
      emptyWeekRem: 15,

      // internal
      __roTimer: null,
      mutationObserver: null,
      weekMaxItemsPerWeek: [],
    }
  },
  mounted() {
    window.addEventListener('resize', this.onResize)

    // источник данных — prop requests (мэпим в items)
    this.items = this.mapRequestsToItems(this.requests || [])
    this.computeStackIndexes()
    this.items.forEach(i => { if (!i.color) i.color = this.defaultColor; this.updateItemStyle(i) })
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize)
    if (this.mutationObserver) this.mutationObserver.disconnect()
  },
  watch: {
    showDate() {
    },
    requests: {
      handler(newVal) {
        this.items = this.mapRequestsToItems(newVal || [])
        this.computeStackIndexes()
        this.items.forEach(i => { if (!i.color) i.color = this.defaultColor; this.updateItemStyle(i) })
        this.$emit('items-updated', this.items.slice())
      },
      deep: true
    },
    items: {
      handler() {
        this.computeStackIndexes()
        this.items.forEach(i => { if (!i.color) i.color = this.defaultColor; this.updateItemStyle(i) })
      },
      deep: true
    }
  },
  computed: {
    itemsSorted() {
      setTimeout(()=>this.updateWeekMaxItemsPerDay(), 100)
      return [...this.items].sort((a,b) => new Date(a.startDate) - new Date(b.startDate))
    },
    cssVars() {
      const root = this.rootFontSize()
      const calRem = this.pxToRem(this.calendarMinHeight || this.dayMinHeightDefault, root)
      return {
        '--cv-calendar-min-height': `${calRem}rem`
      }
    }
  },
  methods: {

    // Преобразование списка заявок (requests) в формат событий календаря
    mapRequestsToItems(reqs) {
      if (!Array.isArray(reqs)) return []
      return reqs.map(r => {
        const id = String(r.id || r.uid || Date.now())

        const userVals = Array.isArray(r.user_value) ? r.user_value : []
        const modelVals = Array.isArray(r.model_value) ? r.model_value : []

        // Ищем в user_value первую строку с ISO-датой
        let dateStr = null
        for (const v of userVals) {
          if (typeof v !== 'string') continue
          const isoMatch = v.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/)
          if (isoMatch) { dateStr = isoMatch[0]; break }
          const dateOnly = v.match(/\d{4}-\d{2}-\d{2}/)
          if (!dateStr && dateOnly) dateStr = dateOnly[0]
        }

        if (!dateStr && r.dt_created) dateStr = r.dt_created
        if (!dateStr) dateStr = (new Date()).toISOString()

        const startDate = dateStr
        const endDate = dateStr

        // Title: сначала пытаемся взять из model_value поле с лейблом "Заголовок" или подобное
        let title = null
        const titleFromModel = modelVals.find(m => m && (m.type === 'input' || m.type === 'text') && /Заголовок|title|Title/i.test(m.label || ''))
        if (titleFromModel && titleFromModel.value) title = titleFromModel.value

        // Иначе используем имя/первую текстовую строку из user_value (если есть)
        if (!title) {
          const candidate = userVals.find(v => typeof v === 'string' && v.trim() && !/\d{4}-\d{2}-\d{2}T/.test(v))
          if (candidate) title = candidate
        }

        if (!title) title = `Заявка ${id}`

        // Tooltip: собираем читаемую сводку из user_value и model_value
        const tooltipParts = []
        if (Array.isArray(r.user_value)) {
          r.user_value.forEach(v => {
            if (typeof v === 'string') {
              // не дублируем дату
              if (!v.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z/)) tooltipParts.push(v + '\n')
            } else if (Array.isArray(v)) {
              // вложенные массивы (например файлы)
              const files = v.map(x => (x && x.url) ? "<a target='_blank' class='main__a' href='" + x.url + "'>Ссылка на файл</a>" : (typeof x === 'string' ? x : JSON.stringify(x)))
              if (files.length) tooltipParts.push(files.join(', '))
            } else if (v && typeof v === 'object' && v.name) {
              tooltipParts.push(v.name)
            }
          })
        }

        const tooltip = tooltipParts.filter(Boolean).join(' — ')

        const color = this.defaultColor

        const item = {
          id,
          title,
          startDate,
          endDate,
          tooltip,
          color,
          classes: []
        }

        // Устанавливаем стиль карточки
        try { this.updateItemStyle(item) } catch(e) {}

        return item
      })
    },

    updateWeekMaxItemsPerDay() {
      const root = document.querySelector('.cv-weeks');
      if (!root) {
        // console.warn('[updateWeekHeight] .cv-weeks not found');
        return;
      }

      const weeks = Array.from(root.getElementsByClassName('cv-week'));
      weeks.forEach(weekEl => {
        const dayEls = Array.from(weekEl.getElementsByClassName('cv-day'));
        const daysCount = dayEls.length;
        const counts = new Array(daysCount).fill(0);

        // Предвычисляем горизонтальные центры дней для fallback-метода
        const dayCenters = dayEls.map(d => {
          const r = d.getBoundingClientRect();
          return r.left + r.width / 2;
        });

        const items = Array.from(weekEl.getElementsByClassName('cv-item'));

        items.forEach(item => {
          const offM = item.className.match(/(?:^|\s)offset(\d+)(?:\s|$)/);
          const spM  = item.className.match(/(?:^|\s)span(\d+)(?:\s|$)/);
          const offset = offM ? parseInt(offM[1], 10) : null;
          const span = spM ? Math.max(1, parseInt(spM[1], 10)) : 1;

          if (offset !== null && offset >= 0 && offset < daysCount) {
            const end = Math.min(daysCount - 1, offset + span - 1);
            for (let i = offset; i <= end; i++) counts[i]++;
            return;
          }

          if (!dayCenters.length) return;

          const r = item.getBoundingClientRect();
          const itemCenter = r.left + r.width / 2;

          let bestIdx = 0;
          let bestDist = Infinity;
          dayCenters.forEach((c, idx) => {
            const dist = Math.abs(c - itemCenter);
            if (dist < bestDist) {
              bestDist = dist;
              bestIdx = idx;
            }
          });

          const endIdx = Math.min(daysCount - 1, bestIdx + span - 1);
          for (let i = bestIdx; i <= endIdx; i++) counts[i]++;
        });

        const maxPerWeek = counts.length ? Math.max(...counts) : 0;
        if (maxPerWeek > 0) {
          weekEl.style.setProperty('min-height', `calc(${maxPerWeek} * 4.2rem)`, 'important');
        } else {
          weekEl.style.minHeight = '';
        }
      });
    },

    rootFontSize() {
      if (typeof window === 'undefined') return 16
      const fs = getComputedStyle(document.documentElement).fontSize
      const parsed = parseFloat(fs)
      return Number.isFinite(parsed) ? parsed : 16
    },
    pxToRem(px, root = null) {
      const r = root || this.rootFontSize()
      return +(px / r).toFixed(4)
    },

    onChangeRange(payload) { this.$emit('change-range', payload) },
    onRefresh(payload) { this.$emit('refresh', payload) },
    onHeaderInput(d) { 
      this.setShowDate(d);
      setTimeout(()=>this.updateWeekMaxItemsPerDay(), 10)
    },
    computeStackIndexes() {
      const groups = {}
      this.items.forEach(it => {
        if (!it || !it.startDate) return
        const key = ('' + it.startDate).slice(0,10)
        if (!groups[key]) groups[key] = []
        groups[key].push(it)
      })
      for (const key in groups) {
        groups[key].sort((a,b) => new Date(a.startDate) - new Date(b.startDate))
        groups[key].forEach((it, idx) => { it._stackIndex = idx })
      }
      this.items.forEach(it => { if (it && it._stackIndex === undefined) it._stackIndex = 0 })
    },

    updateItemStyle(item) {
      const color = item.color || this.defaultColor
      const text = this.getContrastColor(color)
      const idx = (item._stackIndex && Number.isFinite(item._stackIndex)) ? item._stackIndex : 0
      const translatePx = idx * (this.extraStackOffset)
      const root = this.rootFontSize()
      const translateRem = this.pxToRem(translatePx, root)
      item.style = `background-color: ${color}; color: ${text}; transform: translateY(${translateRem}rem);`
    },

    getContrastColor(hex) {
      if (!hex) return '#fff'
      const h = hex.replace('#','')
      const r = parseInt(h.substring(0,2),16)
      const g = parseInt(h.substring(2,4),16)
      const b = parseInt(h.substring(4,6),16)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      return luminance > 0.6 ? '#000' : '#fff'
    },

    emptyForm() {
      const d = new Date()
      const iso = d.toISOString().slice(0,10)
      return { id: null, title: '', startDate: iso, endDate: iso, startTime: '', color: this.defaultColor, tooltip: '' }
    },

    setShowDate(d) { this.showDate = d },

    onClickDate(...args) {
      // Быстрое добавление отключено — просто эмитим событие с датой
      let dateArg = args[0]
      if (Array.isArray(dateArg) && dateArg.length) dateArg = dateArg[0]
      const iso = (new Date(dateArg)).toISOString().slice(0,10)
      this.$emit('date-clicked', { date: iso, background: this.defaultColor })
    },
    onClickItem(...args) {
      let calendarItem = Array.isArray(args[0]) ? args[0][0] : args[0]
      this.openEvent(calendarItem)
    },

    openEvent(item) {
      this.activeEvent = item
      this.viewEventVisible = true
      this.$emit('open-event', { event: item, background: item.color || this.defaultColor })
    },

    /* Добавление/редактирование событий полностью закомментировано по запросу пользователя
    openAddDialog(startIso = null) {
      this.editing = false
      this.form = Object.assign({}, this.emptyForm())
      if (startIso) { this.form.startDate = startIso; this.form.endDate = startIso }
      this.dialogVisible = true
    },

    editEvent(item) {
      this.dialogVisible = true
      this.viewEventVisible = false
      this.editing = true
      this.form = {
        id: item.id,
        title: item.title,
        startDate: (new Date(item.startDate)).toISOString().slice(0,10),
        endDate: item.endDate ? (new Date(item.endDate)).toISOString().slice(0,10) : (new Date(item.startDate)).toISOString().slice(0,10),
        startTime: item.startTime || '',
        color: item.color || this.defaultColor,
        tooltip: item.tooltip || ''
      }
    },

    saveEvent() {
      // функционал сохранения закомментирован
    },

    removeEvent() {
      // функционал удаления закомментирован
    },
    */

    buildCountsByDate() {
      const counts = {}
      this.items.forEach(it => {
        let d = it.startDate
        if (!d) return
        const key = ('' + d).slice(0,10)
        counts[key] = (counts[key] || 0) + 1
      })
      return counts
    },

    computeWeekHeightsPx(daysContainer) {
      const dayCells = Array.from(daysContainer.querySelectorAll('.cv-day'))
      if (!dayCells.length) return []

      const groups = []
      for (let i = 0; i < dayCells.length; i += 7) groups.push(dayCells.slice(i, i + 7))

      const root = this.rootFontSize()
      const libraryTopPx = (this.libraryTopEm * root) + (this.libraryTopExtraPx || 0)
      const perStack = Math.max(libraryTopPx, this.itemMinHeightPx + this.gapPx) + this.extraStackOffset
      const base = Math.max(this.baseDayHeight, this.itemMinHeightPx)
      const safety = Math.max(Math.round(base * 0.1), 20)

      const weekHeights = groups.map(weekDays => {
        let maxRenderedItemHeight = 0
        let maxCount = 0
        weekDays.forEach(d => {
          const items = Array.from(d.querySelectorAll('.cv-item'))
          if (items.length > maxCount) maxCount = items.length
          if (items.length) {
            let h = 0
            items.forEach(it => { try { h += it.getBoundingClientRect().height } catch(e) {} })
            if (h > maxRenderedItemHeight) maxRenderedItemHeight = h
          }
        })

        if (maxRenderedItemHeight > 0) {
          const pad = Math.round( (this.pxToRem(this.itemMinHeightPx, root) * root) * 0.6 )
          return Math.max(Math.round(maxRenderedItemHeight + pad), Math.round(base + (Math.max(1,maxCount) - 1) * perStack + safety))
        }

        const stacks = Math.max(1, maxCount)
        return Math.round(base + (stacks - 1) * perStack + safety)
      })

      return weekHeights
    },
    weeksInCurrentView() {
      const d = new Date(this.showDate)
      const year = d.getFullYear()
      const month = d.getMonth()
      const first = new Date(year, month, 1)
      const last = new Date(year, month + 1, 0)
      const firstWeekday = first.getDay()
      const daysInMonth = last.getDate()
      const weeks = Math.ceil((firstWeekday + daysInMonth) / 7)
      return weeks
    },

    formatShort(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString()
    },
    formatFull(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleString()
    },

    closeDialog() { /* закомментировано */ }
  }
}
</script>

<style scoped>
/* layout */
.page {
  display: flex;
  grid-template-columns: 18.75rem 1fr;
  gap: 1rem;
  padding: 1rem;
  color: var(--color-0);
  background: var(--blue-3);
  border-radius: 0.5rem;
  min-height: 86vh;
  height: 100%;
  flex-direction: row;
  flex-wrap: nowrap;
}
.sidebar {
  background: #212121;
  padding: 1rem;
  display:flex;
  flex-direction:column;
  gap:0.5rem;
  width: 20rem;
}
.sidebar h2 { margin:0 0 0.375rem 0; font-size:1.125rem; }
.events-list { 
  list-style:none; 
  padding:0; 
  margin:0; 
  display:flex; 
  flex-direction:column; 
  gap:0.5rem; 


  overflow:auto; 
}
.events-list__item { display:flex; gap:0.625rem; align-items:center; padding:0.5rem; border-radius:0.5rem; cursor:pointer; transition: background .12s; }
.events-list__item:hover { background: var(--blue-3); }
.dot { width:0.75rem; height:0.75rem; border-radius:50%; flex:0 0 0.75rem; }
.meta .title { font-weight:600; font-size:0.875rem; }
.meta .date { font-size:0.75rem; color:#475569; }
.empty { color:#64748b; font-size:0.8125rem; margin-bottom:0.5rem; }

/* main/calendar */
.main { 
  min-height: 70vh; 
  width: 100%;
}
.nice-calendar { border-radius: none; overflow: visible; }

/* custom header */
.custom-header { padding: 1rem 0.5rem; background: #212121; border: none; }

/* overrides для vue-simple-calendar */
::v-deep(.nice-calendar),
::v-deep(.cv),
::v-deep(.cv-wrapper),
::v-deep(.cv-body),
::v-deep(.cv-days) {
  height: auto !important;
  min-height: var(--cv-calendar-min-height, 37.5rem) !important;
  overflow: visible !important;
}

::v-deep(.cv-week) {
  min-height: var(--cv-week-min-height, auto) !important;
  transition: min-height 240ms ease;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: unset;
}
::v-deep(.cv-week) {
  display: flex;     /* дни в строке располагаются в строчку */
  min-height: 0;
  position: relative; /* если внутренние элементы позиционируются относительно недели */
}

::v-deep(.cv-day) {
  min-height: var(--cv-day-min-height, 5rem) !important;
  height: auto !important;
  display: flex;
  flex-direction: column;
  transition: min-height 240ms ease;
}

/* контейнер событий */
::v-deep(.cv-items) {
  display: flex !important;
  flex-direction: column !important;
  gap: 0.875rem !important;
  padding: 0.625rem !important;
  box-sizing: border-box !important;
  border: none !important;
}

/* карточка события */
::v-deep(.cv-item) {
  min-height: 3rem !important;
  display: flex !important;
  align-items: center !important;
  padding: 0.75rem 0.875rem !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
  white-space: nowrap !important;
  text-overflow: ellipsis !important;
  border: none !important;

}

/* styling for empty-week (visual only) */
::v-deep(.cv-day.empty-week) {
  opacity: 0.95;
}

/* theme */
::v-deep(.cv-header-nav), .cv-header .periodLabel { display: flex !important; }
::v-deep(.theme-default .cv-day.past) { background-color: #212121; }
::v-deep(.theme-default .cv-header), ::v-deep(.theme-default .cv-header-day) { background-color: #212121; }
::v-deep(.theme-default .cv-day.past) { background-color: #272e39; }
::v-deep(.theme-default .cv-day.outsideOfMonth) { background-color: #212121; }
::v-deep(.theme-default .cv-day.today) { background-color: #7386a4; }

/* modal / buttons */
.btn { background: var(--blue-1); color: var(--color-0); display:flex; align-items:center; justify-content:center; padding:0.5rem 0.75rem; border-radius:0.5rem; }
.btn.primary { background: var(--blue-1); color: var(--color-0); }
.btn.danger { background:#fee2e2; color:#991b1b; }

.modal-backdrop { position:fixed; inset:0; display:flex; align-items:center; justify-content:center; background:rgba(2,6,23,0.35); z-index:3000; }
.modal { background:#212121; padding:1rem; border-radius:0.5rem; width:26.25rem; }
.modal h3 { 
  margin-top:0; 
  text-align: left; 
  margin-bottom: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.modal form { display:flex; flex-direction:column; gap:0.625rem; }
.modal label { display:flex; flex-direction:column; font-size:0.8125rem; color: white;}
.modal .color-label { width: fit-content}
.modal input, .modal input[type="text"], .modal input[type="date"], .modal input[type="time"], .modal input[type="color"] { 
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: none;
  margin-top: 0.5rem;
  background-color: var(--grey-0);
  color: var(--color-0);
}
.modal input:focus {
  border: none;
  outline: none
}

.modal input[type="color"] {
  width: 4rem;
  height: 2rem;
  padding: 0.3rem 0.5rem;
}
input[type="date" i]::-webkit-calendar-picker-indicator, input[type="time" i]::-webkit-calendar-picker-indicator  {
  filter: invert(1)
}

.modal-actions { display:flex; gap:0.5rem; justify-content:flex-end; margin-top:1rem; }

.muted { color:#64748b; font-size:0.75rem; }
.calendar__container { padding: 1rem; display:flex; flex-direction:column; gap:1rem; }
.main { min-height: calc(var(--cv-calendar-min-height, 37.5rem) + 1.25rem); overflow: visible; }


::v-deep(.calendar-wrapper) {
  display: flex;
  flex-direction: column;
  height: 80vh;
  min-height: 200px;
}

/* сам компонент должен заполнять родителя */
::v-deep(#calendar-container > .calendar-fullheight),
::v-deep(#calendar-container > .cv-wrapper) {
  display: flex;
  flex-direction: column;
  min-height: 0; /* важно для корректного скролла внутри flex-элемента */
}

/* контейнер с неделями тянется */
::v-deep(.cv-weeks) {
  display: flex;
  flex-direction: column;
  min-height: 0;     /* предотвращает переполнение flex-контейнера */

}

/* каждая неделя занимает одинаковую долю высоты */
::v-deep(.cv-week) {
  display: flex;     /* дни в строке располагаются в строчку */
  min-height: 0;
  position: relative; /* если внутренние элементы позиционируются относительно недели */
}

/* каждый день равномерно растягивается внутри недели */
::v-deep(.cv-day) {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* если вы хотите скроллить недели при большом количестве элементов вместо изменения высоты строк */
::v-deep(.cv-weeks.scrollable) {
  overflow-y: auto;
}

::v-deep(.cv-wrapper) {
  height: 100% !important;
}

::v-deep(.startTime), ::v-deep(.endTime) {
  display: none;
}

.modal__p {
  text-align: left;
}

::v-deep(.previousPeriod), ::v-deep(.currentPeriod), ::v-deep(.nextPeriod), ::v-deep(.previousYear), ::v-deep(.nextYear)  {
  background: transparent
}
::v-deep(.periodLabel) {
  display: none;
}

::v-deep(.custom-header) {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 0.5rem;
  justify-content: center;
}
::v-deep(.cv-header-nav) {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 0.5rem;
  justify-content: center;
}
.main__a {
  color: var(--blue-0);
  text-decoration: none
}

@media (max-width: 768px) {
  .page {
    flex-direction: column;
    width: 100%;
  }
  .sidebar {
    width: 100%;
  }
}

</style>
